import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  AlertDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar, FilterTabs, PaginationControls, TABLE_PAGE_SIZE_OPTIONS } from '../components/ListControls';

// ─── Types ─────────────────────────────────────────────────────────────────────

type FacilityStatus = 'active' | 'paused';

interface Facility {
  id: string;
  name: string;
  collateralTokens: string[];
  settlementTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  status: FacilityStatus;
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const FACILITIES: Facility[] = [
  { id: 'facility-1', name: 'USDC Core Facility', collateralTokens: ['FMM', 'ABN'], settlementTokens: ['USDC'], interestRate: '5.5%', maxLTV: '67%', liquidationThreshold: '150%', utilization: '60%', status: 'active' },
  { id: 'facility-2', name: 'ABN Yield Facility', collateralTokens: ['ABN'], settlementTokens: ['USDC', 'USD1'], interestRate: '4.2%', maxLTV: '77%', liquidationThreshold: '130%', utilization: '40%', status: 'active' },
  { id: 'facility-3', name: 'SNG Stable Facility', collateralTokens: ['SNG'], settlementTokens: ['USDC', 'USDT'], interestRate: '3.8%', maxLTV: '91%', liquidationThreshold: '110%', utilization: '87.5%', status: 'active' },
  { id: 'facility-4', name: 'High Risk Alt Facility', collateralTokens: ['APX', 'MSM'], settlementTokens: ['USDC'], interestRate: '12.5%', maxLTV: '50%', liquidationThreshold: '200%', utilization: '16.6%', status: 'paused' },
  { id: 'facility-5', name: 'APX Opportunity Facility', collateralTokens: ['APX'], settlementTokens: ['USDC', 'USD1'], interestRate: '9.8%', maxLTV: '55%', liquidationThreshold: '180%', utilization: '30%', status: 'active' },
  { id: 'facility-6', name: 'FMM Reserve Facility', collateralTokens: ['FMM'], settlementTokens: ['USDC', 'USDT'], interestRate: '5.0%', maxLTV: '72%', liquidationThreshold: '135%', utilization: '58%', status: 'active' },
  { id: 'facility-7', name: 'MSM Income Facility', collateralTokens: ['MSM', 'SBC'], settlementTokens: ['USDT'], interestRate: '6.5%', maxLTV: '68%', liquidationThreshold: '150%', utilization: '42%', status: 'active' },
  { id: 'facility-8', name: 'SBC Capital Facility', collateralTokens: ['SBC'], settlementTokens: ['USD1', 'USDC'], interestRate: '8.3%', maxLTV: '60%', liquidationThreshold: '165%', utilization: '35%', status: 'active' },
  { id: 'facility-9', name: 'Meridian Credit Facility', collateralTokens: ['ABN', 'SNG'], settlementTokens: ['USDC', 'USDT', 'USD1'], interestRate: '3.2%', maxLTV: '90%', liquidationThreshold: '112%', utilization: '80%', status: 'active' },
  { id: 'facility-10', name: 'Prime Credit Facility', collateralTokens: ['SNG', 'FMM'], settlementTokens: ['USDC'], interestRate: '6.2%', maxLTV: '75%', liquidationThreshold: '140%', utilization: '55%', status: 'active' },
  { id: 'facility-11', name: 'Summit Lending Facility', collateralTokens: ['ABN', 'APX', 'MSM'], settlementTokens: ['USDC', 'USD1'], interestRate: '4.5%', maxLTV: '78%', liquidationThreshold: '128%', utilization: '68%', status: 'paused' },
  { id: 'facility-12', name: 'Horizon Fixed Facility', collateralTokens: ['ABN', 'FMM'], settlementTokens: ['USDC'], interestRate: '3.9%', maxLTV: '82%', liquidationThreshold: '122%', utilization: '72%', status: 'active' },
  { id: 'facility-13', name: 'Quantum Growth Facility', collateralTokens: ['APX', 'SBC', 'MSM'], settlementTokens: ['USDT', 'USD1'], interestRate: '10.2%', maxLTV: '52%', liquidationThreshold: '192%', utilization: '28%', status: 'active' },
  { id: 'facility-14', name: 'Atlas Prime Facility', collateralTokens: ['SNG', 'ABN'], settlementTokens: ['USDC', 'USDT'], interestRate: '4.8%', maxLTV: '80%', liquidationThreshold: '125%', utilization: '64%', status: 'active' },
  { id: 'facility-15', name: 'Nexus Multi Facility', collateralTokens: ['FMM', 'SNG', 'SBC'], settlementTokens: ['USDC', 'USD1', 'USDT'], interestRate: '3.5%', maxLTV: '88%', liquidationThreshold: '114%', utilization: '75%', status: 'active' },
  { id: 'facility-16', name: 'Forge Income Facility', collateralTokens: ['MSM'], settlementTokens: ['USDC'], interestRate: '7.1%', maxLTV: '65%', liquidationThreshold: '154%', utilization: '48%', status: 'active' },
  { id: 'facility-17', name: 'Ember Short Facility', collateralTokens: ['APX', 'FMM'], settlementTokens: ['USDT'], interestRate: '11.0%', maxLTV: '48%', liquidationThreshold: '208%', utilization: '22%', status: 'paused' },
  { id: 'facility-18', name: 'Crescent Balanced Facility', collateralTokens: ['ABN', 'MSM', 'SNG'], settlementTokens: ['USDC', 'USD1'], interestRate: '5.3%', maxLTV: '74%', liquidationThreshold: '136%', utilization: '61%', status: 'active' },
  { id: 'facility-19', name: 'Solaris Flex Facility', collateralTokens: ['SBC', 'ABN'], settlementTokens: ['USDC', 'USDT'], interestRate: '6.8%', maxLTV: '70%', liquidationThreshold: '143%', utilization: '52%', status: 'active' },
  { id: 'facility-20', name: 'Titan Reserve Facility', collateralTokens: ['SNG', 'FMM', 'ABN'], settlementTokens: ['USD1'], interestRate: '4.0%', maxLTV: '85%', liquidationThreshold: '118%', utilization: '78%', status: 'active' },
  { id: 'facility-21', name: 'Nova Opportunity Facility', collateralTokens: ['APX', 'SBC'], settlementTokens: ['USDC', 'USD1'], interestRate: '9.2%', maxLTV: '56%', liquidationThreshold: '178%', utilization: '33%', status: 'active' },
  { id: 'facility-22', name: 'Stellar Income Facility', collateralTokens: ['MSM', 'FMM'], settlementTokens: ['USDT', 'USDC'], interestRate: '5.8%', maxLTV: '71%', liquidationThreshold: '141%', utilization: '57%', status: 'paused' },
  { id: 'facility-23', name: 'Vertex Capital Facility', collateralTokens: ['ABN', 'SBC', 'APX'], settlementTokens: ['USDC'], interestRate: '7.5%', maxLTV: '63%', liquidationThreshold: '158%', utilization: '44%', status: 'active' },
  { id: 'facility-24', name: 'Zenith Credit Facility', collateralTokens: ['SNG', 'MSM'], settlementTokens: ['USDC', 'USDT', 'USD1'], interestRate: '3.6%', maxLTV: '87%', liquidationThreshold: '115%', utilization: '82%', status: 'active' },
  { id: 'facility-25', name: 'Pinnacle Yield Facility', collateralTokens: ['FMM', 'ABN', 'SNG'], settlementTokens: ['USDC', 'USD1'], interestRate: '4.3%', maxLTV: '79%', liquidationThreshold: '127%', utilization: '66%', status: 'active' },
];

const TOKEN_BALANCES: Record<string, number> = {
  USDC: 50000,
  USDT: 45000,
  DAI: 150000,
  ABN: 12500,
  SNG: 85000,
  FMM: 50000,
  APX: 3200,
  MSM: 80000,
  SBC: 125000,
};

const STATUS_CONFIG: Record<FacilityStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', className: 'bg-yellow-100 text-yellow-700' },
};

const STEPS = ['Select Credit Facility', 'Select Settlement Token', 'Deposit Collateral', 'Review & Confirm'] as const;

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CreateMarginAccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFacilityId = searchParams.get('facilityId');
  const preselectedFacility = preselectedFacilityId ? FACILITIES.find((v) => v.id === preselectedFacilityId) : null;

  const [currentStep, setCurrentStep] = useState(preselectedFacility ? 1 : 0);
  const [selectedFacility, setSelectedFacility] = useState<string | null>(preselectedFacility?.id ?? null);
  const [selectedSettlementToken, setSelectedSettlementToken] = useState('');
  const [selectedCollateralToken, setSelectedCollateralToken] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [created, setCreated] = useState(false);
  const [accountId] = useState(() => `ma-${Math.floor(Math.random() * 9000 + 1000)}`);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [facilityPage, setFacilityPage] = useState(1);
  const [facilityPageSize, setFacilityPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  const selectedFacilityData = FACILITIES.find((v) => v.id === selectedFacility);
  const walletBalance = TOKEN_BALANCES[selectedCollateralToken] ?? 0;
  const depositNum = parseFloat(depositAmount || '0');

  const filteredFacilities = useMemo(() => {
    let result = FACILITIES;
    if (statusFilter !== 'all') {
      result = result.filter((v) => v.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          [...v.collateralTokens, ...v.settlementTokens].join(' ').toLowerCase().includes(q),
      );
    }
    return result;
  }, [search, statusFilter]);

  const paginatedFacilities = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredFacilities.length / facilityPageSize));
    const safe = Math.min(facilityPage, totalPages);
    return filteredFacilities.slice((safe - 1) * facilityPageSize, safe * facilityPageSize);
  }, [filteredFacilities, facilityPage, facilityPageSize]);

  // Reset to page 1 when filters change. Comparing prev state during render
  // is the React-recommended pattern for derived state resets.
  const facilityFilterKey = `${search}|${statusFilter}`;
  const [prevFacilityFilterKey, setPrevFacilityFilterKey] = useState(facilityFilterKey);
  if (prevFacilityFilterKey !== facilityFilterKey) {
    setPrevFacilityFilterKey(facilityFilterKey);
    setFacilityPage(1);
  }

  function canProceed(): boolean {
    switch (currentStep) {
      case 0: return selectedFacility !== null;
      case 1: return selectedSettlementToken !== '';
      case 2: return selectedCollateralToken !== '' && depositNum > 0 && depositNum <= walletBalance;
      case 3: return true;
      default: return false;
    }
  }

  function handleNext() {
    if (currentStep < STEPS.length - 1) setCurrentStep((s) => s + 1);
  }

  function handleBack() {
    if (currentStep === 1 && preselectedFacility) {
      navigate(`/credit-facilities/${preselectedFacility.id}`);
      return;
    }
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  function handleConfirm() {
    setConfirming(true);
    setTimeout(() => {
      setConfirming(false);
      setCreated(true);
      toast.success('Margin account created successfully!');
    }, 2000);
  }

  if (created) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex items-start gap-3">
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Create Margin Account</h1>
            <p className="text-sm text-muted-foreground">Your new margin account has been created.</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-10 w-10 text-green-600" />
              </div>
              <div className="text-center">
                <h2 className="text-xl font-bold">Account Created!</h2>
                <p className="mt-2 text-sm text-muted-foreground">Your margin account is ready to use.</p>
              </div>

              <div className="w-full max-w-md rounded-xl border border-border p-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Account ID</span>
                  <span className="font-mono font-semibold">{accountId}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Credit Facility</span>
                  <span className="font-medium">{selectedFacilityData?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Settlement Token</span>
                  <span className="font-medium">{selectedSettlementToken}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Collateral Token</span>
                  <span className="font-medium">{selectedCollateralToken}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Initial Deposit</span>
                  <span className="font-medium">{depositAmount} {selectedCollateralToken}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/deposit')}>
                  <Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="mr-2 h-4 w-4" />
                  Add More Collateral
                </Button>
                <Button onClick={() => navigate('/credit-facilities')}>
                  <Icon name="streamline:share-link" className="mr-2 h-4 w-4" />
                  View Facilities
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Create Margin Account</h1>
          <p className="text-sm text-muted-foreground">Set up a new margin account in a few simple steps.</p>
        </div>
      </div>

      {/* Step indicator + progress bar */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-foreground">Step {currentStep + 1}</span>
          <span className="text-muted-foreground">of {STEPS.length}: {STEPS[currentStep]}</span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted">
          <div
            className="h-2 rounded-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          />
        </div>
        <div className="hidden sm:flex justify-between">
          {STEPS.map((step, i) => (
            <span key={step} className={`text-xs ${i <= currentStep ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              {step}
            </span>
          ))}
        </div>
      </div>

      {/* Step content */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>{STEPS[currentStep]}</CardTitle>
          <CardDescription>
            {currentStep === 0 && 'Choose the credit facility for your new margin account.'}
            {currentStep === 1 && 'Select the settlement token you want to borrow.'}
            {currentStep === 2 && 'Choose a collateral token and set your initial deposit.'}
            {currentStep === 3 && 'Review your selections and confirm account creation.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {/* Step 1: Select Credit Facility */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <SearchBar value={search} onChange={setSearch} placeholder="Search credit facilities..." className="sm:w-64" />
                <FilterTabs
                  filters={[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'paused', label: 'Paused' },
                  ]}
                  active={statusFilter}
                  onChange={setStatusFilter}
                  counts={{
                    all: FACILITIES.length,
                    active: FACILITIES.filter((v) => v.status === 'active').length,
                    paused: FACILITIES.filter((v) => v.status === 'paused').length,
                  }}
                />
              </div>

              {paginatedFacilities.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No credit facilities found matching your search.</p>
              ) : (
                <div className="space-y-3">
                  {paginatedFacilities.map((facility) => {
                    const status = STATUS_CONFIG[facility.status];
                    return (
                      <label
                        key={facility.id}
                        className={`flex cursor-pointer items-center gap-4 rounded-xl border p-4 transition-colors ${
                          selectedFacility === facility.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-accent/50'
                        } ${facility.status === 'paused' ? 'opacity-60' : ''}`}
                      >
                        <input
                          type="radio"
                          name="facility"
                          value={facility.id}
                          checked={selectedFacility === facility.id}
                          onChange={() => {
                            setSelectedFacility(facility.id);
                            setSelectedSettlementToken('');
                            setSelectedCollateralToken('');
                            setDepositAmount('');
                          }}
                          className="h-4 w-4 accent-primary"
                        />
                        <Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{facility.name}</span>
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span>Collateral: {facility.collateralTokens.join(', ')}</span>
                            <span>Settlement: {facility.settlementTokens.join(', ')}</span>
                            <span>Rate: {facility.interestRate}</span>
                            <span>LTV: {facility.maxLTV}</span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <PaginationControls
                page={facilityPage}
                setPage={setFacilityPage}
                pageSize={facilityPageSize}
                setPageSize={setFacilityPageSize}
                totalItems={filteredFacilities.length}
                label="facilities"
              />
            </div>
          )}

          {/* Step 2: Select Settlement Token */}
          {currentStep === 1 && selectedFacilityData && (
            <div className="space-y-6">
              {preselectedFacility && (
                <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <Icon name="streamline:bank" className="h-5 w-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Creating account in: <span className="font-semibold">{selectedFacilityData.name}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Rate: {selectedFacilityData.interestRate} · LTV: {selectedFacilityData.maxLTV} · Collateral: {selectedFacilityData.collateralTokens.join(', ')}
                    </p>
                  </div>
                  <button
                    onClick={() => { setCurrentStep(0); }}
                    className="text-xs text-primary hover:underline flex-shrink-0"
                  >
                    Change
                  </button>
                </div>
              )}

              <div className="rounded-xl border border-border p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">Settlement Token</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Choose which token you want to borrow from this facility.
                </p>
                <select
                  value={selectedSettlementToken}
                  onChange={(e) => setSelectedSettlementToken(e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">Select a settlement token…</option>
                  {selectedFacilityData.settlementTokens.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-xl border border-border p-5 sm:p-6">
                <h4 className="text-sm font-semibold mb-3">Facility Details</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <span className="text-xs text-muted-foreground">Collateral Tokens</span>
                    <div className="mt-1 text-sm font-bold">{selectedFacilityData.collateralTokens.join(', ')}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Max LTV</span>
                    <div className="mt-1 text-sm font-bold">{selectedFacilityData.maxLTV}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Interest Rate</span>
                    <div className="mt-1 text-sm font-bold">{selectedFacilityData.interestRate}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Utilization</span>
                    <div className="mt-1 text-sm font-bold">{selectedFacilityData.utilization}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Deposit Collateral */}
          {currentStep === 2 && selectedFacilityData && (
            <div className="space-y-6">
              <div>
                <label className="mb-3 block text-sm font-medium">Collateral Token</label>
                <div className="space-y-2">
                  {selectedFacilityData.collateralTokens.map((t) => (
                    <label
                      key={t}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                        selectedCollateralToken === t
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-accent/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="collateralToken"
                        value={t}
                        checked={selectedCollateralToken === t}
                        onChange={() => { setSelectedCollateralToken(t); setDepositAmount(''); }}
                        className="h-4 w-4 accent-primary"
                      />
                      <div className="flex-1 flex items-center justify-between">
                        <span className="font-semibold">{t}</span>
                        <span className="text-xs text-muted-foreground">
                          Balance: {(TOKEN_BALANCES[t] ?? 0).toLocaleString()}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {selectedCollateralToken && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Deposit Amount ({selectedCollateralToken})</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="any"
                      className={INPUT_CLASS}
                    />
                    <button
                      onClick={() => setDepositAmount(String(walletBalance))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-primary hover:underline"
                    >
                      MAX
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Balance: {walletBalance.toLocaleString()} {selectedCollateralToken}</span>
                    {depositNum > walletBalance && (
                      <span className="text-red-500">Exceeds balance</span>
                    )}
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-border p-5">
                <h4 className="mb-3 text-sm font-semibold">Deposit Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Credit Facility</span>
                    <span className="font-medium">{selectedFacilityData.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Settlement Token</span>
                    <span className="font-medium">{selectedSettlementToken}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Collateral Token</span>
                    <span className="font-medium">{selectedCollateralToken || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium">{depositNum > 0 ? `${depositNum.toLocaleString()} ${selectedCollateralToken}` : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 3 && selectedFacilityData && (
            <div className="space-y-6">
              <div className="rounded-xl border border-border p-5 space-y-4">
                <ReviewRow icon={<Icon name="streamline:bank" className="h-4 w-4 text-muted-foreground" />} label="Credit Facility" value={selectedFacilityData.name} />
                <ReviewRow icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-4 w-4 text-muted-foreground" />} label="Settlement Token" value={selectedSettlementToken} />
                <ReviewRow icon={<Icon name="streamline:coins-stack" className="h-4 w-4 text-muted-foreground" />} label="Collateral Token" value={selectedCollateralToken} />
                <ReviewRow icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-4 w-4 text-muted-foreground" />} label="Initial Deposit" value={`${depositAmount} ${selectedCollateralToken}`} />
                <ReviewRow icon={<Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 text-muted-foreground" />} label="Interest Rate" value={selectedFacilityData.interestRate} />
                <ReviewRow icon={<Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 text-muted-foreground" />} label="Max LTV" value={selectedFacilityData.maxLTV} />
                <ReviewRow icon={<Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 text-muted-foreground" />} label="Liquidation Threshold" value={selectedFacilityData.liquidationThreshold} />
              </div>

              <Alert>
                <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4" />
                <AlertDescription>
                  By confirming, you agree to the terms and conditions of the selected facility. Your initial deposit will be locked as collateral.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="mr-2 h-4 w-4" />
          Back
        </Button>
        {currentStep < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={!canProceed()}>
            Next
            <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleConfirm} disabled={confirming || !canProceed()}>
            {confirming ? <><Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Confirm & Create'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ReviewRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <div className="flex-1 flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
    </div>
  );
}
